import { derived } from "svelte/store";
import { countryStore } from "../stores/country";
import { languageStore } from "../stores/language";
import { type SupportedCountryCode, campaignCountries } from "./countries";
import type { Language } from "../stores/language";
import { defaultCountry } from "../stores/country";
import SUPPORTED_LANGUAGES, { defaultLanguage } from "./languages";
import { isPrimaryCampaign } from "./countries";

export const contactEmail = "help@fooji.com";

const intlTermsOfService: Record<Language, string> = {
  en: "/IntlLCToSEN.pdf",
  es: "/IntlLCToSES.pdf",
  fr: "/IntlLCToSES.pdf"
}

const termsOfService: Record<SupportedCountryCode, Record<Language, string>> = {
  US: {
    en: "https://littlecaesars.com/en-us/legal/terms-of-service/",
    es: "https://littlecaesars.com/es-us/legal/terms-of-service/",
    fr: "https://littlecaesars.com/en-us/legal/terms-of-service/"
  },
  MX: {
    en: "https://littlecaesars.com/es-mx/legal/terms-of-service/",
    es: "https://littlecaesars.com/es-mx/legal/terms-of-service/",
    fr: "https://littlecaesars.com/es-mx/legal/terms-of-service/"
  },
  CA: {
    en: "https://littlecaesars.ca/fr-ca/legal/terms-of-service/",
    es: "https://littlecaesars.ca/fr-ca/legal/terms-of-service/",
    fr: "https://littlecaesars.ca/fr-ca/legal/terms-of-service/"
  },
  CL: intlTermsOfService,
  DO: intlTermsOfService,
  GT: intlTermsOfService,
  HN: intlTermsOfService,
  NI: intlTermsOfService,
  SV: intlTermsOfService,
}

const intlPrivacyPolicy: Record<Language, string> = {
  en: "/IntlLCPrivacyPolicyEN.pdf",
  es: "/IntlLCPrivacyPolicyES.pdf",
  fr: "/IntlLCPrivacyPolicyES.pdf"
}

const privacyPolicy: Record<SupportedCountryCode, Record<Language, string>> = {
  US: {
    en: "https://privacy.littlecaesars.com/us-privacy-policy/en-us",
    es: "https://privacy.littlecaesars.com/us-privacy-policy/es-us",
    fr: "https://privacy.littlecaesars.com/us-privacy-policy/en-us"
  },
  MX: {
    en: "https://privacy.littlecaesars.com/mx-privacy-policies/es-mx",
    es: "https://privacy.littlecaesars.com/mx-privacy-policies/es-mx",
    fr: "https://privacy.littlecaesars.com/mx-privacy-policies/es-mx"
  },
  CA: {
    en: "https://privacy.littlecaesars.com/ca-privacy-policies/en-ca",
    es: "https://privacy.littlecaesars.com/ca-privacy-policies/en-ca",
    fr: "https://privacy.littlecaesars.com/ca-privacy-policies/fr-ca"
  },
  CL: intlPrivacyPolicy,
  DO: intlPrivacyPolicy,
  GT: intlPrivacyPolicy,
  HN: intlPrivacyPolicy,
  NI: intlPrivacyPolicy,
  SV: intlPrivacyPolicy,
}

const usStatePrivacyPolicy: Record<SupportedCountryCode, Record<Language, string>> = {
  US: {
    en: "https://littlecaesars.com/en-us/legal/privacy-policy/#s12",
    es: "https://littlecaesars.com/es-us/legal/privacy-policy/#s12",
    fr: "https://littlecaesars.com/en-us/legal/privacy-policy/#s12"
  }
}

// TODO: this needs to be removed from the checkbox label
const incentiveLoyaltyProgramNotice: Record<SupportedCountryCode, string> = {
  US: "https://information.littlecaesars.com/en-us/ccpa-financial-incentive#:~:text=Participation%2520in%2520our%2520Incentive/Loyalty%2520Programs%2520is%2520always%2520optional,%2520and%2520you",
}

const officialRules: Record<Language, string> = {
  en: "/LCRules.pdf?v=20251031112800",
  es: "/LCRulesES.pdf?v=20251031112800",
  fr: "/LCRulesFR.pdf?v=20251031112800"
}

// TODO: missing international version of official rules
const intlOfficialRules: Record<Language, string> = {
  en: "/IntlLCRulesES.pdf?v=20251028101600",
  es: "/IntlLCRulesES.pdf?v=20251028101600",
  fr: "/IntlLCRulesES.pdf?v=20251028101600"
}
const contactLittleCaesars: Record<Language, string> = {
  en: "https://littlecaesars.com/en-us/contact-us/customers/",
  es: "https://littlecaesars.com/es-us/contact-us/customers/",
  fr: "https://littlecaesars.com/en-us/contact-us/customers/"
}

const nonParticipatingStores: Record<SupportedCountryCode, string> = {
  US: "/non_participating_stores_us.pdf"
}

const supportedLanguages = [...SUPPORTED_LANGUAGES.map(language => language.key)];

export const urls = derived([countryStore, languageStore], ([$country, $language]) => {
  const country = campaignCountries.includes($country) ? $country : defaultCountry;
  const language = supportedLanguages.includes($language) ? $language : defaultLanguage;

  return {
    primaryCampaign: "https://callofduty.littlecaesars.com",
    internationalCampaign: "https://callofduty-intl.littlecaesars.com",
    activisionSupport: "https://support.activision.com/",
    officialRules: isPrimaryCampaign ? officialRules[language] : intlOfficialRules[language],
    termsOfService: termsOfService[country][language],
    privacyPolicy: privacyPolicy[country][language],
    usStatePrivacyPolicy: usStatePrivacyPolicy[country]?.[language],
    incentiveLoyaltyProgramNotice: incentiveLoyaltyProgramNotice[country],
    nonParticipatingStores: nonParticipatingStores[country],
    contactUs: `mailto:${contactEmail}`,
    contactLittleCaesars: contactLittleCaesars[language],
    social: {
      facebook: "https://www.facebook.com/LittleCaesars",
      twitter: "https://x.com/littlecaesars",
      instagram: "https://www.instagram.com/LittleCaesars",
      youtube: "https://www.youtube.com/@LittleCaesarsPizza",
      tiktok: "https://www.tiktok.com/@littlecaesarspizza#:~:text=Little%20Caesars%20(@littlecaesarspizza)%20on"
    }
  }
})
